import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, CircularProgress, Divider, FormControl, Grid, InputLabel, LinearProgress, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';

interface HostedNodeProps {
  type: 'endpoint' | 'service';
  node: string;
  onClose?: () => void | undefined;
  secondaryBtn?: () => void | undefined;
}
const HostedNode = ({ node, type = 'endpoint', onClose, secondaryBtn }: HostedNodeProps) => {
  const api = useApi();

  const [selectedAwsRegion, setSelectedAwsRegion] = useState('');
  const [vpcCidr, setVpcCidr] = useState('100.101.0.0/24');
  const [peerIp, setPeerIp] = useState('');
  const [peerPrefix, setPeerPrefix] = useState('');
  const [fileName, setFileName] = useState('');

  const { isLoading: awsLoading, data: awsData } = useQuery(
    ['awsRegions'],
    api.listAwsRegionsApiV1ListAwsRegionsGet.bind(null, {}),
    { enabled: true },
  );

  const { data: downloadData, refetch: downloadRefetch, isSuccess: downloadIsSuccess, isFetching: downloadIsFetching } = useQuery(
    ['downloadHsnVpnConfig'],
    api.downloadHsnVpnConfigApiV1DownloadHsnVpnConfigFileNameGet.bind(null, fileName),
    { enabled: false, cacheTime: 0, initialData: undefined },
  );

  const handleDownload = async () => {
    try {
      const response = await downloadRefetch();

      if (response?.data) {
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */ }
        {/* @ts-ignore */ }
        const blob = new Blob([response.data], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}`);
        document.body.appendChild(link);
        link.click();
        link?.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Failed to download VPN configuration.');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error downloading VPN configuration.');
    }
  };

  const { mutate: generateMutate, isLoading: generateIsLoading } = useMutation(api.generateHsnVpnConfigApiV1GenerateHsnVpnConfigPost, {
    onSuccess(res) {
      toast.success('Endpoint Node address received.');
      setFileName(res.data.toString());
    },
    onError: apiError,
  });

  const handlePrepare = () => {
    generateMutate({
      node_id: node,
      form_factor: 'hosted_aws',
      hosted_region: selectedAwsRegion,
      hosted_vpc_cidr: vpcCidr,
      hosted_peer_ip: peerIp,
      hosted_peer_prefix: peerPrefix,
    });
  };

  const endpointFormFactor = useMutation(api.setEndpointNodeFormFactorApiV1SetEndpointNodeFormFactorPost, {
    onSuccess() {
      toast.success('Endpoint Node form factor saved.');
      onClose?.();
    },
    onError: apiError,
  });

  const serviceFormFactor = useMutation(api.setServiceNodeFormFactorApiV1SetServiceNodeFormFactorPost, {
    onSuccess() {
      toast.success('Service Node form factor saved.');
      onClose?.();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    if (type === 'endpoint') {
      endpointFormFactor.mutate({
        form_factor: 'hosted_aws',
        hosted_region: selectedAwsRegion,
        hosted_vpc_cidr: vpcCidr,
        hosted_peer_ip: peerIp,
        hosted_peer_prefix: peerPrefix,
        node_id: node,
      });
    } else {
      serviceFormFactor.mutate({
        form_factor: 'hosted_aws',
        hosted_region: selectedAwsRegion,
        hosted_vpc_cidr: vpcCidr,
        hosted_peer_ip: peerIp,
        hosted_peer_prefix: peerPrefix,
        node_id: node,
      });
    }
  };

  if (awsLoading) {
    return <Loading />;
  }

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 5 }}>
        Configure your hosted service by selecting an AWS region, setting up network parameters, and preparing a VPN configuration for download.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="aws-regions-label">AWS Region</InputLabel>
            <Select
              labelId="aws-regions-label"
              id="aws-regions"
              label="AWS Region"
              value={selectedAwsRegion}
              onChange={(e) => setSelectedAwsRegion(e.target.value)}
            >
              {awsData?.data.map((region: string) => (
                <MenuItem value={region} key={region}>
                  {region}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              id="vpcCidr"
              label="VPC CIDR"
              variant="outlined"
              value={vpcCidr}
              onChange={(e) => setVpcCidr(e.target.value)}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 8 }}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              id="peerIp"
              label="Peer IP"
              variant="outlined"
              value={peerIp}
              onChange={(e) => setPeerIp(e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              id="peerPrefix"
              label="Peer Prefix"
              variant="outlined"
              value={peerPrefix}
              onChange={(e) => setPeerPrefix(e.target.value)}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
        <Button
          variant="outlined"
          type="submit"
          onClick={handlePrepare}
          disabled={generateIsLoading}
          endIcon={generateIsLoading ? <CircularProgress size={20} /> : null}
        >
          Prepare VPN for Download
        </Button>
        <Button
          // onClick={handleDownload}
          download
          target='_blank'
          href={`${import.meta.env.VITE_API}/download-hsn-vpn-config/${fileName}`}
          variant="text"
          color="primary"
          disabled={fileName === ''}
          endIcon={<DownloadIcon fontSize="small" />}
        >
          Download VPN Configuration
        </Button>
      </Box>
      {downloadIsFetching && <LinearProgress variant="indeterminate" sx={{ width: '100%', mt: 5 }} />}
      <Divider sx={{ my: 5, width: '100%' }} />
      <Box display="flex" justifyContent={secondaryBtn ? 'space-between' : 'center'}>
        {secondaryBtn && (
          <Button
            variant="outlined"
            onClick={secondaryBtn}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Save Form Factor
        </Button>
      </Box>
    </Box>
  );
};

export default HostedNode;